// Het planbord van Nog in te plannen: de "te plannen"-bak (verhuisd uit het Rooster,
// Joris 25 sep) boven hetzelfde RoosterGrid als het Rooster, als droppunt.
// Klap je een project open, dan staat het bord direct onder die projectrij (inline) en
// toont de bak alleen dat project — geen twintig projecten naar beneden scrollen.
// Zonder gekozen project staat het ingeklapt onderaan (alle ongeplande taken).
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { Person as PersonIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { getMonday } from '../../utils/weekUtils';
import WeekSelector from '../../ui-components/WeekSelector';
import useRooster, { groepeerPersonen, STAGIAIRS_SLEUTEL } from '../PlannerTodo/useRooster';
import RoosterGrid, { RoosterWeergave, RoosterZoek } from '../PlannerTodo/RoosterGrid';
import TePlannenBak from '../PlannerTodo/TePlannenBak';
import { lokaleDatum } from '../PlannerTodo/plannerDatums';

const FONT = '"Inter", system-ui, sans-serif';
const TEKST = '#1f2937';
const GEDEMPT = '#6b7280';
const ROL_VOOR_TAB = { editors: ['Editor'], animators: ['Animator'] };

const isoVan = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Vanaf welke dag plant "snel toewijzen"? De gelinkte dag (?datum= uit Capaciteit) alleen
 * zolang die in de getoonde week/weken valt en de gelinkte persoon geselecteerd is; blader
 * je naar een andere week, dan de eerste werkdag (maandag) van de getoonde week — nooit
 * buiten wat je ziet.
 */
export function toewijsVanaf({ linkDatum = null, linkPersoon = null, selectedPerson = null, weekStart, numWeken = 1 }) {
  const maandag = getMonday(weekStart);
  if (!linkDatum || !linkPersoon || linkPersoon !== selectedPerson) return maandag;
  const link = linkDatum instanceof Date ? linkDatum : lokaleDatum(linkDatum);
  if (!link || Number.isNaN(link.getTime())) return maandag;
  const eind = new Date(maandag);
  eind.setDate(eind.getDate() + numWeken * 7 - 1);
  const iso = isoVan(link);
  return iso >= isoVan(maandag) && iso <= isoVan(eind) ? link : maandag;
}

const leesOpslag = (k, standaard) => { try { return localStorage.getItem(k) ?? standaard; } catch { return standaard; } };
const zetOpslag = (k, v) => { try { localStorage.setItem(k, v); } catch { /* privémodus */ } };

/**
 * @param project      { id, label } gekozen in de lijst, of null (= alle projecten)
 * @param onWisProject filter opheffen
 * @param tab          'alle' | 'editors' | 'animators' — zet de rolfilter mee
 * @param linkPersoon / linkDatum  uit ?persoon=&datum= (Capaciteit/Rooster → "werk inplannen")
 * @param onGewijzigd  na een toewijzing: het overzicht laten hertellen
 * @param inline       onder een uitgeklapte projectrij: lagere hoogte, geen losse kop
 * @param leegProject  tekst/link voor een lege bak van het gekozen project (zie TePlannenBak)
 * @param onInklappen  onderaan (zonder project): het bord weer tot één regel inklappen
 */
export default function Planbord({
  project, onWisProject, tab = 'alle', linkPersoon = null, linkDatum = null, onGewijzigd, inline = false, leegProject, onInklappen,
}) {
  const startDatum = lokaleDatum(linkDatum);
  const [weekStart, setWeekStart] = useState(() => getMonday(startDatum || new Date()));
  const [numWeken, setNumWeken] = useState(() => {
    const n = Number(leesOpslag('inplannenWeken', 2));
    return [1, 2, 4].includes(n) ? n : 2;
  });
  const kiesWeken = (n) => { setNumWeken(n); zetOpslag('inplannenWeken', String(n)); };
  const [sortering, setSortering] = useState('ruimte');
  const [toonFreelancers, setToonFreelancers] = useState(false);
  // Stagiairs tonen: standaard aan, zelfde onthouden keuze als het Rooster.
  const [toonStagiairs, setToonStagiairs] = useState(() => leesOpslag(STAGIAIRS_SLEUTEL, '1') !== '0');
  const kiesStagiairs = (aan) => { setToonStagiairs(aan); zetOpslag(STAGIAIRS_SLEUTEL, aan ? '1' : '0'); };
  const [functionFilter, setFunctionFilter] = useState(() => ROL_VOOR_TAB[tab] || []);
  useEffect(() => { setFunctionFilter(ROL_VOOR_TAB[tab] || []); }, [tab]);
  const [zoekterm, setZoekterm] = useState('');
  const [selectedPerson, setSelectedPerson] = useState(linkPersoon);

  const rooster = useRooster({ weekStart, numWeken, metBak: true, onGewijzigd });
  const { loading, verversend, persons, labels, belasting } = rooster;
  const zoek = zoekterm.trim().toLowerCase();
  const { groepen } = useMemo(() => groepeerPersonen({
    persons, labels, belasting, zoek, toonFreelancers, toonStagiairs, functionFilter, sortering, selectedPerson,
  }), [persons, labels, belasting, zoek, toonFreelancers, toonStagiairs, functionFilter, sortering, selectedPerson]);
  const projectIds = useMemo(() => (project?.id ? [project.id] : []), [project]);
  const naam = selectedPerson ? (labels[selectedPerson] || '') : '';
  // Snel toewijzen plant op de gelinkte dag zolang die in beeld is, anders vanaf de getoonde week.
  const vanaf = toewijsVanaf({ linkDatum: startDatum, linkPersoon, selectedPerson, weekStart, numWeken });

  return (
    <Box component="section" aria-label="Inplannen" sx={{ mt: inline ? 1.5 : 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
        {onInklappen && (
          <Tooltip title="Inklappen">
            <IconButton size="small" aria-label="Planbord inklappen" onClick={onInklappen} sx={{ p: 0.25, ml: -0.5 }}>
              <ExpandMoreIcon sx={{ fontSize: 18, color: GEDEMPT }} />
            </IconButton>
          </Tooltip>
        )}
        <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: TEKST }}>Inplannen</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: GEDEMPT }}>
          {project ? 'sleep een taak van dit project op een persoon en dag' : 'alle ongeplande taken — sleep op een persoon en dag'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {verversend && (
          <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={10} thickness={6} sx={{ color: GEDEMPT }} />
            <Typography component="span" sx={{ fontSize: 11, color: GEDEMPT }}>bijwerken…</Typography>
          </Box>
        )}
        <RoosterWeergave
          rooster={rooster}
          functionFilter={functionFilter} onFunctionFilter={setFunctionFilter}
          toonFreelancers={toonFreelancers} onToonFreelancers={setToonFreelancers}
          toonStagiairs={toonStagiairs} onToonStagiairs={kiesStagiairs}
          numWeken={numWeken} onWeken={kiesWeken}
        />
        <WeekSelector weekStart={weekStart} onChange={setWeekStart} minWidth={150} />
        <Button size="small" variant="outlined" onClick={() => setWeekStart(getMonday(new Date()))}
          sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0, px: 1.25, py: 0.3, borderColor: '#cbd5e1', color: '#334155' }}>
          Vandaag
        </Button>
      </Box>
      <Box sx={{ height: inline ? 'min(62vh, 680px)' : 'min(78vh, 900px)', minHeight: 380, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #eceef1', borderRadius: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <RoosterGrid
            rooster={rooster}
            groepen={groepen}
            selectedPerson={selectedPerson}
            onSelectPerson={setSelectedPerson}
            kopLinks={<RoosterZoek zoekterm={zoekterm} onZoekterm={setZoekterm} sortering={sortering} onSortering={setSortering} />}
            extraBovenRij={(
              <TePlannenBak
                rooster={rooster}
                projectIds={projectIds}
                projectLabel={project?.label}
                onWisProject={onWisProject}
                leegProject={leegProject}
                zoek={zoek}
                selectedPersonName={naam}
                onTaakKlik={selectedPerson ? (t) => rooster.snelToewijzen(t, selectedPerson, vanaf) : undefined}
              />
            )}
          />
        )}
      </Box>
      {selectedPerson && (
        <Box sx={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
          <Chip
            icon={<PersonIcon />}
            label={`Toewijzen aan ${naam} — klik op een taak in de bak`}
            color="primary"
            onDelete={() => setSelectedPerson(null)}
            sx={{ py: 2, px: 1, fontSize: 12, fontWeight: 500, boxShadow: '0 4px 14px rgba(15,23,42,.25)' }}
          />
        </Box>
      )}
    </Box>
  );
}
